class SBundleBuilder extends HTMLElement {
  #ac;
  #selected = [];
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const toggle = target.closest('[data-bundle-toggle]');
      if (toggle) {
        this.#toggle(toggle.closest('[data-bundle-product]'));
        return;
      }

      const remove = target.closest('[data-slot-remove]');
      if (remove) {
        const productId = remove.dataset.productId;
        const card = this.#cardFor(productId);
        if (card) this.#toggle(card);
        return;
      }

      if (target.closest('[data-bundle-submit]')) this.#submit();
    }, opts);

    this.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-bundle-variant]')) return;

      const card = select.closest('[data-bundle-product]');
      const entry = this.#selected.find((item) => item.productId === card?.dataset.productId);
      if (entry) Object.assign(entry, this.#variantInfo(card));
      this.#render();
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #size() {
    return Number.parseInt(this.dataset.bundleSize || '2', 10);
  }

  #cardFor(productId) {
    return Array.from(this.querySelectorAll('[data-bundle-product]')).find((card) => card.dataset.productId === productId) || null;
  }

  #variantInfo(card) {
    const control = card.querySelector('[data-bundle-variant]');
    const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
    return {
      variantId: control?.value || '',
      price: Number.parseInt(option?.dataset.price || '0', 10),
      sizeLabel: option?.dataset.sizeLabel || '',
    };
  }

  #toggle(card) {
    if (!card) return;
    const productId = card.dataset.productId;
    const title = card.dataset.title || '';
    const index = this.#selected.findIndex((item) => item.productId === productId);

    if (index > -1) {
      this.#selected.splice(index, 1);
      this.#announce(this.dataset.removedMessage, title);
    } else if (this.#selected.length >= this.#size) {
      this.#showError(this.dataset.fullMessage || '');
      return;
    } else {
      this.#selected.push({ productId, title, image: card.dataset.image || '', ...this.#variantInfo(card) });
      this.#announce(this.dataset.addedMessage, title);
    }

    this.#showError('');
    this.#render();
  }

  #render() {
    const selectedIds = new Set(this.#selected.map((item) => item.productId));

    this.querySelectorAll('[data-bundle-product]').forEach((card) => {
      const isSelected = selectedIds.has(card.dataset.productId);
      card.classList.toggle('is-selected', isSelected);
      const toggle = card.querySelector('[data-bundle-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(isSelected));
        toggle.textContent = isSelected ? this.dataset.selectedLabel : this.dataset.addLabel;
      }
    });

    this.querySelectorAll('[data-bundle-slot]').forEach((slot, i) => {
      const item = this.#selected[i];
      const media = slot.querySelector('[data-slot-media]');
      const title = slot.querySelector('[data-slot-title]');
      const detail = slot.querySelector('[data-slot-detail]');
      const remove = slot.querySelector('[data-slot-remove]');

      slot.classList.toggle('border-dashed', !item);
      media.replaceChildren();
      if (item?.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.className = 'size-full object-cover';
        media.append(img);
      } else {
        media.textContent = String(i + 1);
      }

      title.textContent = item ? item.title : this.dataset.slotEmpty;
      detail.textContent = item ? [item.sizeLabel, this.#formatMoney(item.price)].filter(Boolean).join(' · ') : '';
      remove.hidden = !item;
      if (item) {
        remove.dataset.productId = item.productId;
        remove.setAttribute('aria-label', (this.dataset.removeItemLabel || '').replace('[title]', item.title));
      }
    });

    const total = this.#selected.reduce((sum, item) => sum + item.price, 0);
    const totalEl = this.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = this.#formatMoney(total);

    const submit = this.querySelector('[data-bundle-submit]');
    if (submit) {
      const remaining = this.#size - this.#selected.length;
      submit.setAttribute('aria-disabled', String(remaining > 0 || this.#busy));
      submit.textContent = remaining > 0
        ? (this.dataset.pickMoreLabel || '').replace('[count]', String(remaining))
        : this.dataset.addBundleLabel;
    }
  }

  async #submit() {
    if (this.#busy || this.#selected.length < this.#size) return;

    const property = this.dataset.bundleProperty || 'Bundle';
    const bundleName = this.dataset.bundleName || '';
    this.#busy = true;
    this.#showError('');
    this.#render();

    try {
      if (window.Shopify?.actions?.updateCart) {
        const lines = this.#selected.map((item) => ({
          merchandiseId: item.variantId,
          quantity: 1,
          attributes: [{ key: property, value: bundleName }],
        }));
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-bundle-builder' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        this.#selected = [];
        await window.Shopify.actions.openCart?.();
      } else {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: this.#selected.map((item) => ({
              id: Number(item.variantId),
              quantity: 1,
              properties: { [property]: bundleName },
            })),
          }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || '');
        }
        window.location.href = this.dataset.cartUrl;
        return;
      }
    } catch (error) {
      this.#showError(error?.message || this.dataset.errorMessage || '');
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = (cents || 0) / 100;
    const group = (number, decimals, thousands, decimal) => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction && Number(fraction) !== 0 ? `${withThousands}${decimal}${fraction}` : withThousands;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  #announce(template, title) {
    const status = this.querySelector('[data-bundle-status]');
    if (status) status.textContent = (template || '').replace('[title]', title);
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('s-bundle-builder')) {
  customElements.define('s-bundle-builder', SBundleBuilder);
}
